import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { format } from "date-fns";
import { find, propEq, values } from "ramda";
import { Maybe } from "true-myth";

import Button, { ButtonColor } from "components/shared/button";
import Input from "components/shared/input";
import Modal from "components/shared/modal";
import PermalinkContext from "helpers/context/permalink";
import { DateFormats } from "helpers/dates";
import useFormOnSubmit from "helpers/hooks/useFormOnSubmit";
import useFormValue from "helpers/hooks/useFormValue";
import { ITemplatesKeyed } from "types/reducers";
import styled from "styles/styled_components";
import { RequestStatus } from "types/requests";
import { ITemplate } from "types/templates";

const StyledWrapper = styled.div`
  margin-bottom: ${props => props.theme.spacing.medium};
`;
interface ISaveTemplateProps {
  onClose: () => void;
  saveTemplate: (location: string, name: string, uuid: string) => void;
  status: RequestStatus;
  templates: ITemplatesKeyed;
}

export default function SaveTemplate({
  onClose,
  saveTemplate,
  status,
  templates,
}: ISaveTemplateProps) {
  const permalink = useContext(PermalinkContext);
  // Form State
  const [name, setName] = useFormValue("");
  const trimmedName = name.trim();
  const [templateToReplace, setReplace] = useState<ITemplate | null>(null);
  const [error, setError] = useState("");

  // Submit
  const onSubmit = useCallback(
    () => {
      setError("");
      // Skip this block if templateToReplace is set, and name matches (user is confirming replacement if so).
      if (!(templateToReplace && trimmedName === templateToReplace.name)) {
        // Otherwise check name doesn't match any existing templates
        const duplicateTemplate = find(
          propEq("name", trimmedName),
          values(templates)
        );
        if (duplicateTemplate) {
          setReplace(duplicateTemplate);
          return;
        }
      }
      const uuid = Maybe.of(templateToReplace)
        .map(template => template.uuid)
        .unwrapOr("");
      saveTemplate(permalink, trimmedName, uuid);
    },
    [trimmedName, templateToReplace, permalink, saveTemplate, templates]
  );
  const handleSubmit = useFormOnSubmit(onSubmit);

  // Check API Status
  useEffect(
    () => {
      if (name && status === RequestStatus.SUCCESS) {
        onClose();
      }
      if (status === RequestStatus.FAILURE) {
        setError("There was an error saving your template.");
      }
    },
    [status]
  );

  let warning: ReactNode;
  const isReplacingDuplicate =
    templateToReplace && trimmedName === templateToReplace.name;
  const isRequesting = status === RequestStatus.REQUESTING;
  if (isReplacingDuplicate && !isRequesting) {
    warning = templateToReplace!.updatedAt.match({
      Just: updated => (
        <>
          A template with this name already exists and was last
          <br />
          saved {format(updated, DateFormats.MONTH_DAY_YEAR)}. Replace to
          overwrite this template.
        </>
      ),
      Nothing: () => (
        <>A template with this name already exists, replace to overwrite.</>
      ),
    });
  }

  let ctaLabel = isReplacingDuplicate ? "Replace" : "Save";
  if (isRequesting) {
    ctaLabel = "Saving...";
  }

  return (
    <Modal title="Save as Template" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <StyledWrapper>
          <Input
            autoFocus
            error={error}
            warning={warning}
            inputName="Template Name"
            onChange={setName}
            placeholder="Template Name"
            value={name}
          />
        </StyledWrapper>
        <Button
          label="Cancel"
          buttonColor={ButtonColor.GRAY}
          onClick={onClose}
        />
        <Button
          label={ctaLabel}
          disabled={!name || isRequesting}
          type="submit"
          buttonColor={ButtonColor.BLUE}
        />
      </form>
    </Modal>
  );
}
